import React, { useState, useEffect, useRef } from "react";
import { Container, Form, Row, Col, Button } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "./UnifiedFormStyles.css";
import AppButton from "../../Components/MerchantButton";
import { extractImageText } from "../../Helpers/API/formsAPI";
import { compressAndUpload } from "../../Helpers/Utils/photoUpload";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";

function WorkingTerminalDetails({ navigation, primaryDetails, setPrimaryDetails, TERMINAL_MODELS, terminalDetails }) {
  // Initialize terminals array from primaryDetails or start empty
  const [terminals, setTerminals] = useState(() => {
    if (primaryDetails.working_terminals && Array.isArray(primaryDetails.working_terminals) && primaryDetails.working_terminals.length > 0) {
      return primaryDetails.working_terminals;
    }
    return [];
  });

  // Current terminal being edited (not yet added to table)
  const [currentTerminal, setCurrentTerminal] = useState({
    terminal_type: [],
    serial_number: "",
    terminal_id: "",
    card_efs: "",
    merchant_id: "",
    not_obtaining_mid_reason: "",
    other_reason_no_mid: "",
    is_defective: "",
    battery_charge: "",
    receipt_photo_temp_path: "",
    terminal_front_photo_temp_path: "",
    terminal_back_photo_temp_path: ""
  });

  const [errors, setErrors] = useState({});
  const [isStickered, setIsStickered] = useState(primaryDetails.is_stickered || "");
  const [isExtractingReceipt, setIsExtractingReceipt] = useState(false);
  const [isExtractingBackPhoto, setIsExtractingBackPhoto] = useState(false);
  const [isUploadingReceipt, setIsUploadingReceipt] = useState(false);
  const [isUploadingFront, setIsUploadingFront] = useState(false);
  const [isUploadingBack, setIsUploadingBack] = useState(false);

  // Refs for file inputs to clear them when needed
  const receiptPhotoRef = useRef(null);
  const frontPhotoRef = useRef(null);
  const backPhotoRef = useRef(null);

    const [isEditing, setIsEditing] = useState(false);
  // Sync terminals and isStickered to primaryDetails
  useEffect(() => {
    setPrimaryDetails((prev) => ({
      ...prev,
      working_terminals: terminals,
      is_stickered: isStickered
    }));
  }, [terminals, isStickered, setPrimaryDetails]);

  // const UNABLE_TO_PERFORM_REASONS = [
  //   "Terminal has been stickered already",
  //   "HO restriction",
  //   "Old terminal model",
  //   "Defective terminal",
  //   "Sticker does not fit",
  //   "Merchant refused",
  //   "TAS has no on-hand terminal sticker",
  //   "Other reasons"
  // ];

  // Handle current terminal field changes
  const handleTerminalChange = (field, value) => {
    setCurrentTerminal((prev) => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: false }));
    }
  };


    const handleReceiptPhotoChange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      // Size check
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Photo exceeds 5MB limit. Please retake the photo.", {
          autoClose: 3000, hideProgressBar: true, theme: "colored"
        });
        e.target.value = '';
        return;
      }

      setIsUploadingReceipt(true);
      setIsExtractingReceipt(true);

      try {
        // Upload to temp folder
        const uploadResult = await compressAndUpload(file);
        if (!uploadResult.success) {
          toast.error(uploadResult.error || "Upload failed. Please try again.");
          e.target.value = '';
          return;
        }

        // Store temp_path
        handleTerminalChange('receipt_photo_temp_path', uploadResult.temp_path);

        // Extract text from image (OCR)
        const result = await extractImageText(file);
        if (result.data) {
          toast.success("Receipt data extracted successfully!");
          if (result.data.card_efs) {
            handleTerminalChange('card_efs', result.data.merchant_id);
          }
          if (result.data.merchant_id) {
            handleTerminalChange('merchant_id', result.data.merchant_id);
          }
          if (result.data.merchant_id && result.data.payment_channel) {
            if (result.data.payment_channel === 'Credit Card') {
              handleTerminalChange('card_efs', result.data.merchant_id);
            } else if (result.data.payment_channel === 'Maya QR' || result.data.payment_channel === 'QR Ph') {
              handleTerminalChange('merchant_id', result.data.merchant_id);
            }
          }
          if (result.data.terminal_id) {
            handleTerminalChange('terminal_id', result.data.terminal_id);
          }
          if (result.data.serial_number) {
            handleTerminalChange('serial_number', result.data.serial_number);
          }
        }
      } catch (error) {
        toast.error("Error processing receipt photo");
        console.error("Error:", error);
      } finally {
        setIsUploadingReceipt(false);
        setIsExtractingReceipt(false);
      }
    };


    const handleTerminalBackPhotoChange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      if (file.size > 5 * 1024 * 1024) {
        toast.error("Photo exceeds 5MB limit. Please retake the photo.", {
          autoClose: 3000, hideProgressBar: true, theme: "colored"
        });
        e.target.value = '';
        return;
      }

      setIsUploadingBack(true);
      setIsExtractingBackPhoto(true);

      try {
        const uploadResult = await compressAndUpload(file);
        if (!uploadResult.success) {
          toast.error(uploadResult.error || "Upload failed. Please try again.");
          e.target.value = '';
          return;
        }

        handleTerminalChange('terminal_back_photo_temp_path', uploadResult.temp_path);

        // Extract serial number
        const result = await extractImageText(file);
        if (result.data) {
          toast.success("Terminal back photo data extracted successfully!");
          if (result.data.serial_number) {
            handleTerminalChange('serial_number', result.data.serial_number);
          }
        }
      } catch (error) {
        toast.error("Error processing terminal back photo");
        console.error("Error:", error);
      } finally {
        setIsUploadingBack(false);
        setIsExtractingBackPhoto(false);
      }
    };

  // Handle checkbox for terminal types
  const handleTerminalTypeChange = (type, checked) => {
    setCurrentTerminal((prev) => {
      const currentTypes = prev.terminal_type || [];
      let updatedTypes;
      
      if (checked) {
        updatedTypes = [...currentTypes, type];
      } else {
        updatedTypes = currentTypes.filter(t => t !== type);
      }
      
      return { ...prev, terminal_type: updatedTypes };
    });
    if (errors.terminal_type) {
      setErrors((prev) => ({ ...prev, terminal_type: false }));
    }
  };


    const handleTerminalFrontPhotoChange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      if (file.size > 5 * 1024 * 1024) {
        toast.error("Photo exceeds 5MB limit. Please retake the photo.", {
          autoClose: 3000, hideProgressBar: true, theme: "colored"
        });
        e.target.value = '';
        return;
      }

      setIsUploadingFront(true);
      try {
        const uploadResult = await compressAndUpload(file);
        if (!uploadResult.success) {
          toast.error(uploadResult.error || "Upload failed. Please try again.");
          e.target.value = '';
          return;
        }
        handleTerminalChange('terminal_front_photo_temp_path', uploadResult.temp_path);
      } catch (error) {
        toast.error("Error uploading front photo");
      } finally {
        setIsUploadingFront(false);
      }
    };

  // Add current terminal to table
  const handleAddTerminal = () => {
    const newErrors = {};
    
    if (!currentTerminal.terminal_type || currentTerminal.terminal_type.length === 0) {
      newErrors.terminal_type = "Terminal type is required";
    }
    if (!currentTerminal.serial_number) {
      newErrors.serial_number = "Serial number is required";
    }
    if (!currentTerminal.terminal_id) {
      newErrors.terminal_id = "Terminal ID is required";
    }
    if (!currentTerminal.card_efs) {
      newErrors.card_efs = "Card EFS is required";
    }
    if (!currentTerminal.merchant_id) {
      newErrors.merchant_id = "QR EFS is required";
    }
    // if (!currentTerminal.not_obtaining_mid_reason) {
    //   newErrors.not_obtaining_mid_reason = "Reason is required";
    // }
    if (currentTerminal.not_obtaining_mid_reason === 'Other reason' && !currentTerminal.other_reason_no_mid) {
      newErrors.other_reason_no_mid = "Other reason is required";
    }
    if (!currentTerminal.battery_charge) {
      newErrors.battery_charge = "Battery charge is required";
    }
    if (!currentTerminal.receipt_photo_temp_path) {
      newErrors.receipt_photo = "Receipt photo is required";
    }
    if (!currentTerminal.terminal_front_photo_temp_path) {
      newErrors.terminal_front_photo = "Front photo is required";
    }
    if (!currentTerminal.terminal_back_photo_temp_path) {
      newErrors.terminal_back_photo = "Back photo is required";
    }
    if (!currentTerminal.is_defective) {
      newErrors.is_defective = "Terminal concern is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fill in all required fields", {
        autoClose: 3000,
        hideProgressBar: true,
        theme: "colored",
      });
      return;
    }

    // Add to terminals array
    setTerminals([...terminals, { ...currentTerminal, id: Date.now() }]);
    
    // Reset current terminal form
    setCurrentTerminal({
      terminal_type: [],
      serial_number: "",
      terminal_id: "",
      card_efs: "",
      merchant_id: "",
      not_obtaining_mid_reason: "",
      other_reason_no_mid: "",
      is_defective: "",
      battery_charge: "",
      receipt_photo_temp_path: "",
      terminal_front_photo_temp_path: "",
      terminal_back_photo_temp_path: ""
    });
    setErrors({});
    
    // Clear file input values
    if (receiptPhotoRef.current) receiptPhotoRef.current.value = '';
    if (frontPhotoRef.current) frontPhotoRef.current.value = '';
    if (backPhotoRef.current) backPhotoRef.current.value = '';
    
    toast.success("Terminal added successfully!");
  };

  // Remove terminal from table
  const handleRemoveTerminal = (terminalId) => {
    setTerminals(terminals.filter(t => t.id !== terminalId));
  };

  const validate = () => {
    // Get the required number of terminals from POA
    const requiredTerminals = parseInt(terminalDetails?.poa) || 0;
    
    // Check if the correct number of terminals is added
    if (terminals.length !== requiredTerminals) {
      toast.error(`Please add exactly ${requiredTerminals} terminal${requiredTerminals !== 1 ? 's' : ''} (POA: ${requiredTerminals})`, {
        autoClose: 3000,
        hideProgressBar: true,
        theme: "colored",
      });
      return false;
    }
    
    // Check if stickering is selected
    if (!isStickered) {
      toast.error("Please select if terminals are stickered", {
        autoClose: 3000,
        hideProgressBar: true,
        theme: "colored",
      });
      return false;
    }
    
    return true;
  };

  const isFormValid = () => {
    const requiredTerminals = parseInt(terminalDetails?.poa) || 0;
    const hasCorrectTerminalCount = terminals.length === requiredTerminals && requiredTerminals > 0;
    const hasStickeringAnswer = !!isStickered;

    return hasCorrectTerminalCount && hasStickeringAnswer;
  };
  const handleBack = () => {
    navigation.previous();
  };

  const handleNext = () => {
  if (!validate()) return;

  // Navigate based on stickering status
  if (isStickered === 'Yes') {
    navigation.go('terminal-stickering-details');
  } else if (isStickered === 'No') {
    navigation.go('terminal-stickering');
  } else {
    navigation.next();
  }
  };



  return (
    <div className="form-bg">
      <Container>
        <ToastContainer />
        <Row className="justify-content-md-center">

          {/* Main Form */}
          <Col md={10}>
              <div className="form-main-header">
                <h4 className="form-title">WORKING TERMINAL DETAILS</h4>
                <p className="form-desc">Please provide information about working terminals</p>
              </div>
              <div className="form-main">
              <div className="form-main-content">
              <Form>
                {/* Form Section for Current Terminal */}
                <div style={{ 
                  border: '2px solid #e0e0e0', 
                  borderRadius: '8px', 
                  padding: '20px', 
                  backgroundColor: '#f9f9f9',
                  marginBottom: '20px'
                }}>
                  <h5 style={{ marginBottom: '20px', color: '#333' }}>
                    {terminals.length === 0 ? 'Terminal No. 1' : `Terminal No. ${terminals.length + 1}`}
                    <span style={{ marginLeft: '10px', fontSize: '14px', color: '#666' }}>
                      (Required: {terminalDetails?.poa || 0} total)
                    </span>
                  </h5>

                {/* Terminal Type */}
                <Form.Group className="mb-4">
                  <Form.Label className="form-label">
                    Type of Terminal <span className="form-required">*</span>
                  </Form.Label>
                  <div className="form-checkbox-group">
                    {TERMINAL_MODELS.map((model) => (
                      <div key={model} className={`form-checkbox-item${currentTerminal.terminal_type && currentTerminal.terminal_type.includes(model) ? " selected" : ""}`}>
                        <div className="form-checkbox-flex">
                          <Form.Check
                            type="checkbox"
                            value={model}
                            label={model}
                            checked={currentTerminal.terminal_type && currentTerminal.terminal_type.includes(model)}
                            onChange={(e) => handleTerminalTypeChange(model, e.target.checked)}
                            className="form-checkbox"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  {errors.terminal_type && (
                    <div className="form-error">{errors.terminal_type}</div>
                  )}
                </Form.Group>

                {/* Photo of Receipt */}
                <Form.Group className="mb-4">
                  <Form.Label className="form-label">
                    Photo of Receipt <span className="form-required">*</span>
                    {(isExtractingReceipt || isUploadingReceipt) && (
                      <span style={{ marginLeft: '10px', color: '#007bff' }}>
                        {isUploadingReceipt ? 'Uploading...' : 'Extracting data...'}
                      </span>
                    )}
                    {currentTerminal.receipt_photo_temp_path && !isUploadingReceipt && !isExtractingReceipt && (
                      <span style={{ marginLeft: '10px', color: '#28a745' }}>✅ Uploaded</span>
                    )}
                  </Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={handleReceiptPhotoChange}
                    className={errors.receipt_photo ? "form-input-error" : ""}
                    disabled={isExtractingReceipt || isUploadingReceipt}
                    ref={receiptPhotoRef}
                  />
                  {errors.receipt_photo && (
                    <div className="form-error">{errors.receipt_photo}</div>
                  )}
                </Form.Group>

                {/* Terminal Front Photo */}
                <Form.Group className="mb-4">
                  <Form.Label className="form-label">
                    Terminal Front Photo <span className="form-required">*</span>
                    {isUploadingFront && (
                      <span style={{ marginLeft: '10px', color: '#007bff' }}>Uploading...</span>
                    )}
                    {currentTerminal.terminal_front_photo_temp_path && !isUploadingFront && (
                      <span style={{ marginLeft: '10px', color: '#28a745' }}>✅ Uploaded</span>
                    )}
                  </Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={handleTerminalFrontPhotoChange}
                    className={errors.terminal_front_photo ? "form-input-error" : ""}
                    disabled={isUploadingFront || isUploadingReceipt || isUploadingBack}
                    ref={frontPhotoRef}
                  />
                  {errors.terminal_front_photo && (
                    <div className="form-error">{errors.terminal_front_photo}</div>
                  )}
                </Form.Group>

                {/* Terminal Back Photo */}
                <Form.Group className="mb-4">
                  <Form.Label className="form-label">
                    Terminal Back Photo <span className="form-required">*</span>
                    {(isExtractingBackPhoto || isUploadingBack) && (
                      <span style={{ marginLeft: '10px', color: '#007bff' }}>
                        {isUploadingBack ? 'Uploading...' : 'Extracting data...'}
                      </span>
                    )}
                    {currentTerminal.terminal_back_photo_temp_path && !isUploadingBack && !isExtractingBackPhoto && (
                      <span style={{ marginLeft: '10px', color: '#28a745' }}>✅ Uploaded</span>
                    )}
                  </Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={handleTerminalBackPhotoChange}
                    className={errors.terminal_back_photo ? "form-input-error" : ""}
                    disabled={isExtractingBackPhoto || isUploadingBack || isUploadingReceipt}
                    ref={backPhotoRef}
                  />
                  {errors.terminal_back_photo && (
                    <div className="form-error">{errors.terminal_back_photo}</div>
                  )}
                </Form.Group>
                
                {/* Serial Number */}
                <Form.Group className="mb-4">
                  <Form.Label className="form-label">
                    Serial Number <span className="form-required">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={currentTerminal.serial_number || ""}
                    onChange={(e) => handleTerminalChange('serial_number', e.target.value)}
                    placeholder="Enter serial number..."
                    className={`form-input${errors.serial_number ? " form-input-error" : ""}`}
                  />
                  {errors.serial_number && (
                    <div className="form-error">{errors.serial_number}</div>
                  )}
                </Form.Group>

                {/* Terminal ID */}
                <Form.Group className="mb-4">
                  <Form.Label className="form-label">
                    Terminal ID <span className="form-required">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={currentTerminal.terminal_id || ""}
                    onChange={(e) => handleTerminalChange('terminal_id', e.target.value)}
                    placeholder="Enter terminal ID..."
                    className={`form-input${errors.terminal_id ? " form-input-error" : ""}`}
                  />
                  {errors.terminal_id && (
                    <div className="form-error">{errors.terminal_id}</div>
                  )}
                </Form.Group>

                {/* Terminal MID (Card EFS) */}
                <Form.Group className="mb-4">
                  <Form.Label className="form-label">
                    Merchant ID for Card Transactions <span className="form-required">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={currentTerminal.card_efs || ""}
                    onChange={(e) => handleTerminalChange('card_efs', e.target.value)}
                    placeholder="Enter Card EFS MID..."
                    className={`form-input${errors.card_efs ? " form-input-error" : ""}`}
                  />
                  {errors.card_efs && (
                    <div className="form-error">{errors.card_efs}</div>
                  )}
                </Form.Group>
                
                {/* Terminal MID (QR EFS) */}
                <Form.Group className="mb-4">
                  <Form.Label className="form-label">
                    Merchant ID for QR Transactions <span className="form-required">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={currentTerminal.merchant_id || ""}
                    onChange={(e) => handleTerminalChange('merchant_id', e.target.value)}
                    placeholder="Enter QR EFS MID..."
                    className={`form-input${errors.merchant_id ? " form-input-error" : ""}`}
                  />
                  {errors.merchant_id && (
                    <div className="form-error">{errors.merchant_id}</div>
                  )}
                </Form.Group>
                
                {/* Reason for not obtaining MID */}
                <Form.Group className="mb-4">
                  <Form.Label className="form-label">
                    Reason for not obtaining MID
                  </Form.Label>
                  <Form.Select
                    value={currentTerminal.not_obtaining_mid_reason || ""}
                    onChange={(e) => handleTerminalChange('not_obtaining_mid_reason', e.target.value)}
                    className={`form-input${errors.not_obtaining_mid_reason ? " form-input-error" : ""}`}
                  >
                    <option value="">Select Reason</option>
                    <option value="Needs Management Approval First">Needs Management Approval First</option>
                    <option value="HO Restriction">HO Restriction</option>
                    <option value="Data Privacy">Data Privacy</option>
                    <option value="Defective Terminal">Defective Terminal</option>
                    <option value="Other reason">Other reason</option>
                  </Form.Select>
                  {errors.not_obtaining_mid_reason && (
                    <div className="form-error">{errors.not_obtaining_mid_reason}</div>
                  )}
                </Form.Group>
                
                {/* Other reason for not obtaining MID */}
                {currentTerminal.not_obtaining_mid_reason === 'Other reason' && (
                  <Form.Group className="mb-4">
                    <Form.Label className="form-label">
                      Other reason for not obtaining MID <span className="form-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={currentTerminal.other_reason_no_mid || ""}
                      onChange={(e) => handleTerminalChange('other_reason_no_mid', e.target.value)}
                      placeholder="Enter other reason..."
                      className={`form-input${errors.other_reason_no_mid ? " form-input-error" : ""}`}
                    />
                    {errors.other_reason_no_mid && (
                      <div className="form-error">{errors.other_reason_no_mid}</div>
                    )}
                  </Form.Group>
                )}
                
                {/* Has Terminal Concern */}
                <Form.Group className="mb-4">
                  <Form.Label className="form-label">
                    Has Terminal Concern <span className="form-required">*</span>
                  </Form.Label>
                  <Form.Select
                    value={currentTerminal.is_defective || ""}
                    onChange={(e) => handleTerminalChange('is_defective', e.target.value)}
                    className={`form-input${errors.is_defective ? " form-input-error" : ""}`}
                  >
                    <option value="">Select Option</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                  {errors.is_defective && (
                    <div className="form-error">{errors.is_defective}</div>
                  )}
                </Form.Group>

                {/* 30% or more battery charge */}
                <Form.Group className="mb-4">
                  <Form.Label className="form-label">
                    50% or more battery charge <span className="form-required">*</span>
                  </Form.Label>
                  <Form.Select
                    value={currentTerminal.battery_charge || ""}
                    onChange={(e) => handleTerminalChange('battery_charge', e.target.value)}
                    className={`form-input${errors.battery_charge ? " form-input-error" : ""}`}
                  >
                    <option value="">Select Option</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                  {errors.battery_charge && (
                    <div className="form-error">{errors.battery_charge}</div>
                  )}
                </Form.Group>

                {/* Add Terminal Button - Disable if required terminals reached */}
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                  <Button 
                    variant="success" 
                    onClick={handleAddTerminal}
                    disabled={terminals.length >= (parseInt(terminalDetails?.poa) || 0)}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px', 
                      margin: '0 auto',
                      opacity: terminals.length >= (parseInt(terminalDetails?.poa) || 0) ? 0.6 : 1
                    }}
                  >
                    <PlusOutlined /> {isEditing ? 'Save Terminal' : 'Add Terminal'}
                  </Button>
                  {terminals.length >= (parseInt(terminalDetails?.poa) || 0) && (
                    <div style={{ marginTop: '10px', color: '#28a745', fontSize: '14px' }}>
                      Required number of terminals ({terminalDetails?.poa || 0}) completed!
                    </div>
                  )}
                </div>
                </div>

                {/* Terminal Stickering - Single question for all terminals */}
                <Form.Group className="mb-4">
                  <Form.Label className="form-label">
                    Are the terminals stickered? <span className="form-required">*</span>
                  </Form.Label>
                  <Form.Select
                    value={isStickered}
                    onChange={(e) => {
                      setIsStickered(e.target.value);
                      if (errors.is_stickered) {
                        setErrors((prev) => ({ ...prev, is_stickered: false }));
                      }
                    }}
                    className={`form-input${errors.is_stickered ? " form-input-error" : ""}`}
                  >
                    <option value="">Select Option</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                  {errors.is_stickered && (
                    <div className="form-error">{errors.is_stickered}</div>
                  )}
                </Form.Group>

                {/* Terminals Table */}
                {terminals.length > 0 && (
                  <div className="mb-4">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                      <h6 className="form-label" style={{ margin: 0 }}>Added Terminals</h6>
                      <span style={{ 
                        fontSize: '14px', 
                        color: terminals.length === (parseInt(terminalDetails?.poa) || 0) ? '#28a745' : '#666',
                        fontWeight: 'bold'
                      }}>
                        {terminals.length} / {terminalDetails?.poa || 0} required
                      </span>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        backgroundColor: '#fff',
                        border: '1px solid #ddd'
                      }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderRight: '1px solid #ddd' }}>No.</th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderRight: '1px solid #ddd' }}>Type</th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderRight: '1px solid #ddd' }}>Serial No.</th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderRight: '1px solid #ddd' }}>Terminal ID</th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderRight: '1px solid #ddd' }}>Merchant ID</th>
                            <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {terminals.map((terminal, index) => (
                            <tr key={terminal.id} style={{ borderBottom: '1px solid #ddd' }}>
                              <td style={{ padding: '12px', borderRight: '1px solid #ddd' }}>{index + 1}</td>
                              <td style={{ padding: '12px', borderRight: '1px solid #ddd' }}>
                                {terminal.terminal_type && terminal.terminal_type.length > 0 
                                  ? terminal.terminal_type.join(', ') 
                                  : '-'}
                              </td>
                              <td style={{ padding: '12px', borderRight: '1px solid #ddd' }}>{terminal.serial_number || '-'}</td>
                              <td style={{ padding: '12px', borderRight: '1px solid #ddd' }}>{terminal.terminal_id || '-'}</td>
                              <td style={{ padding: '12px', borderRight: '1px solid #ddd' }}>{terminal.card_efs || '-'}</td>
                              <td style={{ padding: '12px', textAlign: 'center', display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                <Button 
                                  variant="primary" 
                                  size="sm"
                                  onClick={() => {
                                    setIsEditing(true);
                                    setCurrentTerminal(terminal);
                                    handleRemoveTerminal(terminal.id);
                                  }}
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                                >
                                  Edit
                                </Button>
                                <Button 
                                  variant="danger" 
                                  size="sm"
                                  onClick={() => handleRemoveTerminal(terminal.id)}
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                                >
                                  <DeleteOutlined /> Remove
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="form-btn-row">
                  <AppButton
                    onClick={handleBack}
                    variant="back"
                    className="form-btn variant-secondary"
                  >
                    ← Back 
                  </AppButton>
                  <AppButton
                    onClick={handleNext}
                    variant="next"
                    className="form-btn variant-primary"
                    disabled={!isFormValid()}
                  >
                    Next →
                  </AppButton>
                </div>
              </Form>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default WorkingTerminalDetails;
